import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Package, Plus, Search, Edit2, Trash2, Eye,
  ArrowUpDown, ChevronDown, ChevronUp, LayoutGrid, TableIcon,
  Box, TrendingUp, TrendingDown, AlertTriangle, Beaker
} from 'lucide-react';
import RecipeEditor from './RecipeEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminInventory = ({ getAuthHeaders, franchises = [], initialTab = 'catalog', initialMenuItemId = null }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [catalogItems, setCatalogItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Add/Edit dialog
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    item_name: '', category: '', unit: '', base_unit: 'kg',
    conversion_factor: 1, cost_price: 0, description: '',
    assigned_franchises: []
  });

  // Overview
  const [overview, setOverview] = useState(null);
  const [overviewMode, setOverviewMode] = useState('table');

  // Franchise detail view
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [franchiseStock, setFranchiseStock] = useState([]);
  const [franchiseLogs, setFranchiseLogs] = useState([]);

  const fetchCatalog = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = selectedCategory !== 'all' ? `?category=${encodeURIComponent(selectedCategory)}` : '';
      const response = await axios.get(`${API}/inventory/catalog${params}`, getAuthHeaders());
      setCatalogItems(response.data.items || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Failed to load inventory catalog');
    }
    setIsLoading(false);
  }, [getAuthHeaders, selectedCategory]);

  const fetchOverview = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/inventory/overview`, getAuthHeaders());
      setOverview(response.data);
    } catch (error) {
      toast.error('Failed to load inventory overview');
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  useEffect(() => {
    if (activeTab === 'overview') fetchOverview();
  }, [activeTab, fetchOverview]);

  // Respond to external tab change (e.g., from menu edit shortcut)
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, conversion_factor: parseFloat(formData.conversion_factor) || 1, cost_price: parseFloat(formData.cost_price) || 0 };
      if (editingItem) {
        await axios.put(`${API}/inventory/catalog/${editingItem.item_id}`, payload, getAuthHeaders());
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/inventory/catalog`, payload, getAuthHeaders());
        toast.success('Item added');
      }
      setIsAddOpen(false);
      setEditingItem(null);
      resetForm();
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this inventory item?')) return;
    try {
      await axios.delete(`${API}/inventory/catalog/${itemId}`, getAuthHeaders());
      toast.success('Item deleted');
      fetchCatalog();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      item_name: item.item_name || '',
      category: item.category || '',
      unit: item.unit || '',
      base_unit: item.base_unit || 'kg',
      conversion_factor: item.conversion_factor || 1,
      cost_price: item.cost_price || 0,
      description: item.description || '',
      assigned_franchises: []
    });
    setIsAddOpen(true);
  };

  const resetForm = () => {
    setFormData({ item_name: '', category: '', unit: '', base_unit: 'kg', conversion_factor: 1, cost_price: 0, description: '', assigned_franchises: [] });
  };

  const togglePermission = async (franchiseId, currentValue) => {
    try {
      await axios.put(`${API}/inventory/permissions/${franchiseId}`, { can_manage_inventory: !currentValue }, getAuthHeaders());
      toast.success(`Inventory permission ${!currentValue ? 'enabled' : 'disabled'}`);
      fetchOverview();
    } catch (error) {
      toast.error('Failed to update permission');
    }
  };

  const viewFranchiseDetail = async (franchiseId) => {
    setSelectedFranchise(franchiseId);
    try {
      const [stockRes, logsRes] = await Promise.all([
        axios.get(`${API}/inventory/franchise/${franchiseId}`),
        axios.get(`${API}/inventory/logs/${franchiseId}?limit=30`)
      ]);
      setFranchiseStock(stockRes.data.items || []);
      setFranchiseLogs(logsRes.data.logs || []);
    } catch (error) {
      toast.error('Failed to load franchise details');
    }
  };

  const filteredItems = catalogItems.filter(item =>
    item.item_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const BASE_UNITS = ['kg', 'g', 'L', 'ml', 'pieces', 'packets', 'rolls', 'boxes'];

  return (
    <div data-testid="admin-inventory-section">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-primary">Inventory Management</h2>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-muted/50 p-1 rounded-xl">
          <TabsTrigger value="catalog" data-testid="inventory-catalog-tab" className="rounded-lg px-4">
            <Package className="w-4 h-4 mr-2" /> Catalog
          </TabsTrigger>
          <TabsTrigger value="overview" data-testid="inventory-overview-tab" className="rounded-lg px-4">
            <Eye className="w-4 h-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="recipes" data-testid="inventory-recipes-tab" className="rounded-lg px-4">
            <Beaker className="w-4 h-4 mr-2" /> Recipes
          </TabsTrigger>
        </TabsList>

        {/* ===== CATALOG TAB ===== */}
        <TabsContent value="catalog">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input data-testid="inventory-search" placeholder="Search items..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-10 rounded-xl" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="inventory-category-filter" className="w-48 h-10 rounded-xl">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={isAddOpen} onOpenChange={(open) => { setIsAddOpen(open); if (!open) { setEditingItem(null); resetForm(); } }}>
              <DialogTrigger asChild>
                <Button data-testid="add-inventory-button" className="h-10 bg-primary text-white hover:bg-primary/90 rounded-xl px-6 font-bold">
                  <Plus className="w-4 h-4 mr-2" /> Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-surface max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">{editingItem ? 'Edit' : 'Add'} Inventory Item</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                  <div>
                    <Label>Item Name *</Label>
                    <Input data-testid="inv-form-name" value={formData.item_name} onChange={(e) => setFormData({...formData, item_name: e.target.value})} className="h-10 rounded-xl" required />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Category *</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                        <SelectTrigger data-testid="inv-form-category" className="h-10 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                          <SelectItem value="Finished Goods">Finished Goods</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Display Unit *</Label>
                      <Input data-testid="inv-form-unit" value={formData.unit} onChange={(e) => setFormData({...formData, unit: e.target.value})} placeholder="e.g., Bundle of 5Kg" className="h-10 rounded-xl" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Base Unit</Label>
                      <Select value={formData.base_unit} onValueChange={(v) => setFormData({...formData, base_unit: v})}>
                        <SelectTrigger data-testid="inv-form-base-unit" className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {BASE_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Conversion Factor</Label>
                      <Input data-testid="inv-form-factor" type="number" step="0.01" value={formData.conversion_factor} onChange={(e) => setFormData({...formData, conversion_factor: e.target.value})} className="h-10 rounded-xl" />
                    </div>
                    <div>
                      <Label>Cost Price</Label>
                      <Input data-testid="inv-form-cost" type="number" step="0.01" value={formData.cost_price} onChange={(e) => setFormData({...formData, cost_price: e.target.value})} className="h-10 rounded-xl" />
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input data-testid="inv-form-desc" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="h-10 rounded-xl" />
                  </div>
                  {!editingItem && (
                    <div>
                      <Label className="mb-2 block">Assign to Franchises</Label>
                      <div className="max-h-40 overflow-y-auto border rounded-xl p-2 space-y-1">
                        {franchises.map(f => {
                          const fid = f.franchise_code || f.franchise_id;
                          const isSelected = formData.assigned_franchises.some(af => af.franchise_id === fid);
                          return (
                            <label key={fid} className="flex items-center gap-2 p-1.5 hover:bg-muted/50 rounded cursor-pointer text-sm">
                              <input type="checkbox" checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData({...formData, assigned_franchises: [...formData.assigned_franchises, { franchise_id: fid, franchise_name: f.franchise_name, city: f.city || '', initial_stock: 0 }]});
                                  } else {
                                    setFormData({...formData, assigned_franchises: formData.assigned_franchises.filter(af => af.franchise_id !== fid)});
                                  }
                                }}
                              />
                              <span>{f.franchise_name}</span>
                              {isSelected && (
                                <Input type="number" placeholder="Initial stock" className="h-7 w-24 ml-auto text-xs rounded" value={formData.assigned_franchises.find(af => af.franchise_id === fid)?.initial_stock || 0}
                                  onChange={(e) => {
                                    setFormData({...formData, assigned_franchises: formData.assigned_franchises.map(af => af.franchise_id === fid ? {...af, initial_stock: parseFloat(e.target.value) || 0} : af)});
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <Button type="submit" data-testid="inv-form-submit" className="w-full h-10 bg-primary text-white rounded-xl font-bold">
                    {editingItem ? 'Update Item' : 'Add Item'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Catalog Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Card className="border-border"><CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Items</div>
              <div className="text-2xl font-bold text-primary">{catalogItems.length}</div>
            </CardContent></Card>
            <Card className="border-border"><CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Categories</div>
              <div className="text-2xl font-bold text-primary">{categories.length}</div>
            </CardContent></Card>
            <Card className="border-border"><CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">From RM</div>
              <div className="text-2xl font-bold text-blue-600">{catalogItems.filter(i => i.source === 'raw_material').length}</div>
            </CardContent></Card>
            <Card className="border-border"><CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Custom Added</div>
              <div className="text-2xl font-bold text-green-600">{catalogItems.filter(i => i.source !== 'raw_material').length}</div>
            </CardContent></Card>
          </div>

          {/* Catalog Table */}
          <Card className="border-border">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-bold">Item Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Base Unit</TableHead>
                    <TableHead>Factor</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
                  ) : filteredItems.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No items found</TableCell></TableRow>
                  ) : (
                    filteredItems.map(item => (
                      <TableRow key={item.item_id} data-testid={`catalog-row-${item.item_id}`}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{item.category}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{item.unit}</TableCell>
                        <TableCell className="text-sm font-mono">{item.base_unit}</TableCell>
                        <TableCell className="text-sm font-mono">x{item.conversion_factor}</TableCell>
                        <TableCell className="text-sm">Rs.{item.cost_price?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={item.source === 'raw_material' ? 'secondary' : item.source === 'franchise' ? 'default' : 'outline'} className="text-xs">
                            {item.source === 'raw_material' ? 'RM' : item.source === 'franchise' ? 'Franchise' : 'Admin'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-inv-${item.item_id}`}><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(item.item_id)} data-testid={`delete-inv-${item.item_id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* ===== OVERVIEW TAB ===== */}
        <TabsContent value="overview">
          {overview ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-bold">Cross-Franchise Stock Overview</h3>
                <div className="flex ml-auto bg-muted/50 rounded-lg p-0.5">
                  <Button variant={overviewMode === 'table' ? 'default' : 'ghost'} size="sm" onClick={() => setOverviewMode('table')} data-testid="overview-table-btn">
                    <TableIcon className="w-4 h-4" />
                  </Button>
                  <Button variant={overviewMode === 'cards' ? 'default' : 'ghost'} size="sm" onClick={() => setOverviewMode('cards')} data-testid="overview-cards-btn">
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {overviewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {overview.franchises.map(f => (
                    <Card key={f.franchise_id} className="border-border hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewFranchiseDetail(f.franchise_id)} data-testid={`overview-card-${f.franchise_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-bold text-sm">{f.franchise_name}</h4>
                            <p className="text-xs text-muted-foreground">{f.city}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch checked={f.can_manage_inventory || false} onCheckedChange={() => togglePermission(f.franchise_id, f.can_manage_inventory)} onClick={(e) => e.stopPropagation()} />
                          </div>
                        </div>
                        <div className="flex gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Box className="w-3.5 h-3.5 text-primary" />
                            <span className="font-medium">{f.total_items}</span>
                            <span className="text-muted-foreground">items</span>
                          </div>
                          {f.low_stock_count > 0 && (
                            <div className="flex items-center gap-1 text-amber-600">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              <span className="font-medium">{f.low_stock_count}</span>
                              <span>negative</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-border">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-bold sticky left-0 bg-background z-10">Franchise</TableHead>
                          <TableHead>City</TableHead>
                          <TableHead>Items</TableHead>
                          <TableHead>Negative Stock</TableHead>
                          <TableHead>Can Manage</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {overview.franchises.map(f => (
                          <TableRow key={f.franchise_id} data-testid={`overview-row-${f.franchise_id}`}>
                            <TableCell className="font-medium sticky left-0 bg-background">{f.franchise_name}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{f.city}</TableCell>
                            <TableCell>{f.total_items}</TableCell>
                            <TableCell>
                              {f.low_stock_count > 0 ? (
                                <Badge variant="destructive" className="text-xs">{f.low_stock_count}</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs text-green-600">OK</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Switch checked={f.can_manage_inventory || false} onCheckedChange={() => togglePermission(f.franchise_id, f.can_manage_inventory)} data-testid={`perm-switch-${f.franchise_id}`} />
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="outline" size="sm" onClick={() => viewFranchiseDetail(f.franchise_id)} data-testid={`view-franchise-${f.franchise_id}`}>
                                <Eye className="w-4 h-4 mr-1" /> View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              )}

              {/* Franchise Detail Modal */}
              <Dialog open={!!selectedFranchise} onOpenChange={(open) => { if (!open) setSelectedFranchise(null); }}>
                <DialogContent className="bg-surface max-w-2xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold">
                      {overview?.franchises.find(f => f.franchise_id === selectedFranchise)?.franchise_name || 'Franchise'} — Stock
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Stock</TableHead>
                          <TableHead>Unit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {franchiseStock.map(item => (
                          <TableRow key={item.item_id}>
                            <TableCell className="font-medium">{item.item_name}</TableCell>
                            <TableCell><Badge variant="outline" className="text-xs">{item.category}</Badge></TableCell>
                            <TableCell className={item.current_stock < 0 ? 'text-red-600 font-bold' : 'font-medium'}>
                              {item.current_stock}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{item.base_unit}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {franchiseLogs.length > 0 && (
                      <div>
                        <h4 className="font-bold text-sm mb-2">Recent Activity</h4>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {franchiseLogs.map(log => (
                            <div key={log.log_id} className="flex items-center gap-2 text-sm border-b border-border pb-2">
                              {log.type === 'stock_in' || log.type === 'rm_order_received' ? (
                                <TrendingUp className="w-4 h-4 text-green-600 flex-shrink-0" />
                              ) : (
                                <TrendingDown className="w-4 h-4 text-red-500 flex-shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <span className="font-medium">{log.item_name}</span>
                                <span className="text-muted-foreground ml-1">
                                  {log.previous_stock} → {log.new_stock}
                                </span>
                              </div>
                              <Badge variant="outline" className="text-xs flex-shrink-0">{log.type.replace(/_/g, ' ')}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">Loading overview...</div>
          )}
        </TabsContent>

        {/* ===== RECIPES TAB ===== */}
        <TabsContent value="recipes">
          <RecipeEditor getAuthHeaders={getAuthHeaders} initialMenuItemId={initialMenuItemId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminInventory;
